import { Box, IconButton, styled, Typography } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import CloseIcon from "@material-ui/icons/Close";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { describeCine } from "./cine/cine-metadata";
import { EpocusCinePlayer } from "./cine/epocus-cine-player";
import { EPOCUS_WHITE, rem } from "./epocus-palette";
import type { HydratedExamImage } from "./hooks/use-epocus-exam-images";

// ─── Styled ───────────────────────────────────────────────────────────────────

const Overlay = styled(Box)({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.92)",
  zIndex: 1400,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const CloseBtn = styled(IconButton)({
  position: "absolute",
  top: 12,
  right: 12,
  color: EPOCUS_WHITE,
  zIndex: 1,
});

const ImageArea = styled(Box)({
  flex: 1,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
});

const FullImg = styled("img")({
  maxWidth: "calc(100vw - 88px)",
  maxHeight: "calc(100vh - 120px)",
  objectFit: "contain",
  userSelect: "none",
  pointerEvents: "none",
});

const NavBtn = styled(IconButton)({
  position: "absolute",
  color: EPOCUS_WHITE,
  backgroundColor: "rgba(255,255,255,0.1)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.2)",
  },
  "&:disabled": {
    opacity: 0.2,
  },
  width: 44,
  height: 44,
});

const Caption = styled(Box)({
  width: "100%",
  padding: "8px 16px 16px",
  textAlign: "center",
});

const CaptionText = styled(Typography)({
  fontSize: rem(13),
  color: "rgba(255,255,255,0.7)",
});

const Counter = styled(Typography)({
  fontSize: rem(12),
  color: "rgba(255,255,255,0.45)",
  marginTop: 2,
});

// ─── Component ────────────────────────────────────────────────────────────────

export const EpocusImageLightbox: FC<{
  images: HydratedExamImage[];
  initialIndex: number;
  onClose: () => void;
}> = ({ images, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);

  // Touch swipe tracking
  const touchStartX = useRef<number | null>(null);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(images.length - 1, i + 1));

  // Keyboard navigation — prev/next are re-defined each render but always
  // reference the latest images.length via closure, so we inline them here.
  useEffect(() => {
    const onPrev = () => setIndex((i) => Math.max(0, i - 1));
    const onNext = () => setIndex((i) => Math.min(images.length - 1, i + 1));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      const target = e.target;
      if (
        target instanceof Element &&
        target.closest('[role="slider"], input, textarea')
      )
        return;
      if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, images.length]);

  const image = images[index];
  if (!image) return null;

  return createPortal(
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
        touchStartX.current = null;
        if (dx < -40) next();
        else if (dx > 40) prev();
      }}
    >
      <CloseBtn onClick={onClose} aria-label="Close viewer" size="small">
        <CloseIcon />
      </CloseBtn>

      <ImageArea>
        <NavBtn
          style={{ left: 8 }}
          onClick={prev}
          disabled={index === 0}
          aria-label="Previous image"
          size="small"
        >
          <ArrowBackIosIcon fontSize="small" />
        </NavBtn>

        {image.cine ? (
          <EpocusCinePlayer
            key={image.id}
            imagingInstanceId={image.id}
            metadata={image.cine}
            posterUrl={image.previewUrl}
            label={image.viewLabel ?? `Image ${index + 1}`}
            active
          />
        ) : image.previewUrl ? (
          <FullImg
            src={image.previewUrl}
            alt={image.viewLabel ?? `Image ${index + 1}`}
          />
        ) : (
          <Box
            style={{
              width: 240,
              height: 180,
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              style={{ color: "rgba(255,255,255,0.4)", fontSize: rem(13) }}
            >
              Image unavailable
            </Typography>
          </Box>
        )}

        <NavBtn
          style={{ right: 8 }}
          onClick={next}
          disabled={index === images.length - 1}
          aria-label="Next image"
          size="small"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </NavBtn>
      </ImageArea>

      <Caption>
        {image.viewLabel && <CaptionText>{image.viewLabel}</CaptionText>}
        <Counter>
          {index + 1} / {images.length}
          {image.cine ? ` · ${describeCine(image.cine)}` : ""}
        </Counter>
      </Caption>
    </Overlay>,
    document.body,
  );
};
